import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import FlagIcon from '@mui/icons-material/Flag';

import conversationUIStyles from '../styles/conversationUIStyles';
import { getTherapistReviewerPersonas } from '../../../services/api';

const ReviewPanel = ({
  open,
  onClose,
  conversationId,
  onRequestReview,
  reviewResult,
  reviewLoading,
  reviewError,
  token
}) => {
  const [exportLoading, setExportLoading] = useState(false);
  const [reviewerPersonas, setReviewerPersonas] = useState([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState('');
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const personasFetchedRef = useRef(false);

  // Fetch reviewer personas lazily when panel opens
  useEffect(() => {
    if (open && !personasFetchedRef.current) {
      personasFetchedRef.current = true;
      setLoadingPersonas(true);
      getTherapistReviewerPersonas(token)
        .then(data => {
          setReviewerPersonas(data.personas || []);
        })
        .catch(err => {
          console.error('Failed to load reviewer personas:', err);
        })
        .finally(() => {
          setLoadingPersonas(false);
        });
    }
  }, [open, token]);

  const handleRequestReview = () => {
    if (conversationId && onRequestReview) {
      onRequestReview(conversationId, selectedPersonaId || null);
    }
  };

  const handleExportReview = async () => {
    setExportLoading(true);
    try {
      // Implement export functionality
      console.log('Exporting review:', reviewResult);
      // Convert to PDF or desired format
      setTimeout(() => {
        setExportLoading(false);
      }, 2000);
    } catch (error) {
      console.error('Export error:', error);
      setExportLoading(false);
    }
  };

  const personaSelector = (
    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
      <InputLabel id="reviewer-persona-label">Reviewer Persona</InputLabel>
      <Select
        labelId="reviewer-persona-label"
        value={selectedPersonaId}
        label="Reviewer Persona"
        onChange={(e) => setSelectedPersonaId(e.target.value)}
        disabled={loadingPersonas || reviewLoading}
        MenuProps={{ sx: { zIndex: 1600 } }}
      >
        <MenuItem value="">
          <em>Default Reviewer</em>
        </MenuItem>
        {reviewerPersonas.map((persona) => (
          <MenuItem key={persona._id} value={persona._id}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {persona.name}
              </Typography>
              {persona.description && (
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  {persona.description.length > 80
                    ? persona.description.substring(0, 80) + '...'
                    : persona.description}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
      {loadingPersonas && (
        <Typography variant="caption" sx={{ mt: 0.5, color: '#64748b' }}>
          Loading reviewer personas...
        </Typography>
      )}
    </FormControl>
  );

  const panelStyles = {
    ...conversationUIStyles.slidePanel,
    ...(open && conversationUIStyles.slidePanelOpen)
  };

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          ...conversationUIStyles.backdrop,
          ...(open && conversationUIStyles.backdropVisible)
        }}
      />

      {/* Panel — stopPropagation prevents clicks on interactive elements
           (Select dropdowns, buttons) from bubbling to the backdrop's onClose */}
      <Box sx={panelStyles} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <Box sx={conversationUIStyles.slidePanelHeader}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              AI Review Analysis
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close review panel">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={conversationUIStyles.slidePanelContent}>
          {!reviewResult && !reviewLoading && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <AssignmentIcon sx={{ fontSize: 64, color: '#e2e8f0', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, color: '#475569' }}>
                No Review Available
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>
                Request an AI review to analyze the conversation quality and identify areas for improvement
              </Typography>
              <Box sx={{ textAlign: 'left', mb: 2 }}>
                {personaSelector}
              </Box>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={handleRequestReview}
                sx={{
                  background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)'
                  }
                }}
              >
                Request AI Review
              </Button>
            </Box>
          )}

          {reviewLoading && (
            <Box sx={{ py: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'center' }}>
                Analyzing conversation...
              </Typography>
              <LinearProgress />
              <Typography variant="caption" sx={{ mt: 2, display: 'block', textAlign: 'center', color: '#64748b' }}>
                This may take a few moments
              </Typography>
            </Box>
          )}

          {reviewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {reviewError}
              <Button size="small" onClick={handleRequestReview} sx={{ ml: 2 }}>
                Retry
              </Button>
            </Alert>
          )}

          {reviewResult && (
            <Box>
              {/* Evaluation Section */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TipsAndUpdatesIcon />
                Evaluation
              </Typography>
              <Box sx={{
                p: 2.5,
                mb: 3,
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <Typography
                  variant="body2"
                  sx={{ color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                >
                  {reviewResult.evaluation || 'No evaluation data returned.'}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Recommendations Section */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FlagIcon />
                Recommendations
              </Typography>
              <Box sx={{
                p: 2.5,
                mb: 3,
                backgroundColor: '#FFF8E1',
                borderRadius: '8px',
                border: '1px solid #FFE082'
              }}>
                <Typography
                  variant="body2"
                  sx={{ color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                >
                  {reviewResult.recommendations || 'No recommendations returned.'}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Re-run Review Section */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#475569' }}>
                Re-run Review with Different Persona
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#64748b' }}>
                Select a different reviewer persona and regenerate the review
              </Typography>
              {personaSelector}
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRequestReview}
                disabled={reviewLoading}
                sx={{ mb: 2 }}
              >
                Re-run Review
              </Button>

              {/* Export Button */}
              <Button
                fullWidth
                variant="outlined"
                startIcon={exportLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleExportReview}
                disabled={exportLoading}
                sx={{ mt: 1 }}
              >
                Export Review Report
              </Button>

              {/* Timestamp */}
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: '#94a3b8' }}>
                Review generated {new Date().toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ReviewPanel;
